import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import AddressFields from "../shared/AddressFields";
import CompanyPicker from "./CompanyPicker";
import PositionPicker from "./PositionPicker";
import { formatPhoneNumber } from "../../lib/formatters";

export const EMPTY_CONTACT = {
  first_name: "", last_name: "", company: "", title: "", email: "",
  phone_office: "", phone_mobile: "", street: "", apt: "", city: "", state: "", zip_code: "", notes: "",
};

const Field = ({ id, label, children }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs block">{label}</Label>
    {children}
  </div>
);

const ContactFormDialog = ({ open, onOpenChange, contact, onSaved }) => {
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [saving, setSaving] = useState(false);
  const editing = !!contact?.id;

  useEffect(() => {
    if (open) setForm({ ...EMPTY_CONTACT, ...(contact || {}) });
  }, [open, contact]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // dialog may be portaled from inside another <form> (job editor)
    if (!form.first_name && !form.last_name && !form.company && !form.email) {
      toast.error("Enter at least a name, company or email");
      return;
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY_CONTACT).map((k) => [k, form[k] || ""]));
      const res = editing
        ? await apiClient.put(`/shared-contacts/${contact.id}`, payload)
        : await apiClient.post(`/shared-contacts`, payload);
      toast.success(editing ? "Contact updated" : "Contact added");
      onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save contact");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-form-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Contact" : "Add Contact"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field id="c-first" label="First name"><Input id="c-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} autoFocus autoComplete="given-name" data-testid="contact-first-name" /></Field>
            <Field id="c-last" label="Last name"><Input id="c-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} autoComplete="family-name" data-testid="contact-last-name" /></Field>
            <Field id="c-company" label="Company"><CompanyPicker id="c-company" value={form.company} onChange={(v) => set("company", v)} /></Field>
            <Field id="c-title" label="Title / Position"><PositionPicker id="c-title" value={form.title} onChange={(v) => set("title", v)} /></Field>
            <Field id="c-email" label="Email"><Input id="c-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" data-testid="contact-email" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field id="c-office" label="Office phone"><Input id="c-office" value={form.phone_office} onChange={(e) => set("phone_office", formatPhoneNumber(e.target.value))} autoComplete="tel" maxLength={14} placeholder="(555) 123-4567" data-testid="contact-phone-office" /></Field>
              <Field id="c-mobile" label={<span className="flex items-center justify-between">Mobile phone
                {(form.phone_office || form.phone_mobile) && (
                  <button type="button" className="text-[11px] text-sky-700 hover:underline" onClick={() => setForm((f) => ({ ...f, phone_office: f.phone_mobile, phone_mobile: f.phone_office }))} data-testid="contact-swap-phones">swap ⇄</button>
                )}</span>}>
                <Input id="c-mobile" value={form.phone_mobile} onChange={(e) => set("phone_mobile", formatPhoneNumber(e.target.value))} autoComplete="tel" maxLength={14} placeholder="(555) 123-4567" data-testid="contact-phone-mobile" /></Field>
            </div>
          </div>
          <AddressFields
            values={{ street: form.street, apt: form.apt, city: form.city, state: form.state, zip_code: form.zip_code }}
            onChange={(field, value) => set(field, value)}
            onChangeMany={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            prefix="contact"
            compact
          />
          <Field id="c-notes" label="Notes"><Textarea id="c-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="contact-notes" /></Field>
          <p className="text-xs text-muted-foreground">Contacts with an email are published to everyone's Outlook address list (GAL). Without an email they stay in the app only.</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="contact-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? "Save Changes" : "Add Contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
